import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const DeviceContactImportDialog = ({ open, onClose, onImported }) => {
  const [connections, setConnections] = useState([]);
  const [whatsappId, setWhatsappId] = useState("");
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [importing, setImporting] = useState(false);

  const connected = useMemo(
    () => connections.filter(item => item.channel === "whatsapp" && item.status === "CONNECTED"),
    [connections]
  );

  useEffect(() => {
    if (!open) return;

    const loadConnections = async () => {
      setLoadingConnections(true);
      try {
        const { data } = await api.get("/whatsapp", { params: { session: 0 } });
        const list = Array.isArray(data) ? data : data.whatsapps || [];
        setConnections(list);
        const preferred = list.find(item => item.channel === "whatsapp" && item.status === "CONNECTED" && item.isDefault)
          || list.find(item => item.channel === "whatsapp" && item.status === "CONNECTED");
        setWhatsappId(preferred ? preferred.id : "");
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingConnections(false);
      }
    };

    loadConnections();
  }, [open]);

  const handleImport = async () => {
    if (!whatsappId) {
      toast.warning("Selecione uma conexão conectada.");
      return;
    }

    setImporting(true);
    try {
      const { data } = await api.post("/contacts/import", { whatsappId });
      toast.success(
        `Importação concluída: ${data.created || 0} novos, ${data.updated || 0} atualizados e ${data.ignored || 0} ignorados.`
      );
      if (onImported) onImported(data);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={importing ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Importar contatos do aparelho</DialogTitle>
      <DialogContent dividers>
        <Typography color="textSecondary" paragraph>
          Escolha a conexão do WhatsApp que será usada. Contatos existentes serão preservados e poderão ter o nome atualizado.
        </Typography>

        {loadingConnections ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="device-contact-connection-label">Conexão conectada</InputLabel>
            <Select
              labelId="device-contact-connection-label"
              value={whatsappId}
              onChange={event => setWhatsappId(event.target.value)}
              label="Conexão conectada"
            >
              {connected.map(connection => (
                <MenuItem key={connection.id} value={connection.id}>
                  {connection.name}{connection.isDefault ? " (padrão)" : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!loadingConnections && connected.length === 0 && (
          <Box mt={2} p={2} borderRadius={8} bgcolor="rgba(244, 67, 54, 0.08)">
            <Typography color="error">Nenhuma conexão WhatsApp está conectada no momento.</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={importing}>Cancelar</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleImport}
          disabled={importing || loadingConnections || !whatsappId}
        >
          {importing ? "Importando..." : "Importar contatos"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceContactImportDialog;
